'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import CCPALink from '@/components/CCPALink';
import ConsentPreferenceCenter from '@/components/ConsentPreferenceCenter';
import { detectRegion } from '@/lib/consent';

export default function HomepageFooter() {
  const [showPreferences, setShowPreferences] = useState(false);
  const [region, setRegion] = React.useState<'gdpr' | 'ccpa' | 'none'>('none');

  React.useEffect(() => {
    setRegion(detectRegion());
  }, []);

  return (
    <>
      <footer className="bg-foreground text-white py-16">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <AppLogo size={32} />
                <span className="font-bold text-xl text-white">EzCleanCare</span>
              </div>
              <p className="text-sm text-white/50 leading-relaxed">Professional cleaning services, on demand. Vetted, insured, and guaranteed.</p>
            </div>
            {[
              { title: 'Services', links: ['Standard Clean', 'Deep Clean', 'Move In/Out', 'Commercial'] },
              { title: 'Company', links: ['About Us', 'Careers', 'Press', 'Blog'] },
              { title: 'Support', links: ['Help Center', 'Contact Us', 'Privacy Policy', 'Terms of Service'] },
            ]?.map((col) => (
              <div key={`footer-col-${col?.title}`}>
                <h4 className="text-sm font-semibold text-white mb-4">{col?.title}</h4>
                <ul className="space-y-2">
                  {col?.links?.map((link) => (
                    <li key={`footer-link-${link}`}>
                      <Link href="/" className="text-sm text-white/50 hover:text-white transition-colors">{link}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Privacy & Compliance Links Section */}
          <div className="border-t border-white/10 pt-6 mb-6">
            <h4 className="text-sm font-semibold text-white mb-4">Privacy & Compliance</h4>
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <Link href="/privacy" className="text-sm text-white/50 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link href="/cookies" className="text-sm text-white/50 hover:text-white transition-colors">
                Cookie Policy
              </Link>
              <Link href="/accessibility" className="text-sm text-white/50 hover:text-white transition-colors">
                Accessibility
              </Link>
              <CCPALink />
              <button
                onClick={() => setShowPreferences(true)}
                className="text-sm text-white/50 hover:text-white transition-colors text-left"
              >
                Cookie Preferences
              </button>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/40">© 2026 EzCleanCare Ltd. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link href="/sign-up-login-screen" className="text-xs text-white/40 hover:text-white transition-colors">Customer Login</Link>
              <Link href="/sign-up-login-screen" className="text-xs text-white/40 hover:text-white transition-colors">Cleaner Login</Link>
              <Link href="/admin-dashboard" className="text-xs text-white/40 hover:text-white transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </footer>

      {showPreferences && (
        <ConsentPreferenceCenter
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          region={region}
        />
      )}
    </>
  );
}